import React, { useCallback, useEffect, useRef, useState } from "react"
import { Badge, Button, Card, Col, ProgressBar, Row, Spinner, Table } from "react-bootstrap"
import { toast } from "react-toastify"
import API from "../../common/API"

interface FotoMigrationStatus {
  habilitado: boolean
  enPausa: boolean
  configuracion: {
    raiz: string
    tamanoLote: number
    pausaEntreLotesMs: number
    vaciaBlob: boolean
    verificaEscritura: boolean
  }
  progreso: {
    total: number
    migradas: number
    pendientes: number
    porcentaje: number
    blobsPorVaciar: number
    faseActual: string
  }
  ritmo: {
    fotosPorSegundo: number | null
    horasRestantesEstimadas: number | null
    megabytesEscritos: number
  }
  ultimoLote: {
    migradas: number
    fallidas: number
    segundos: number
    ultimaCedula: string | null
  }
  acumulado: {
    migradas: number
    fallidas: number
  }
  disco: { libreGb: number; totalGb: number } | null
  ultimaCorrida: string | null
  ultimoError: string | null
}

interface VerifyResult {
  verificadas: number
  faltantes: number
  vacias: number
  integra: boolean
  ejemplosFaltantes: string[]
  ejemplosVacias: string[]
}

const formatNumber = (n: number) => n.toLocaleString("es-DO")

const AdminFotoMigrationComponent = () => {
  const [status, setStatus] = useState<FotoMigrationStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [acting, setActing] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const bindStatus = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      const res = await API.getAction("FotoMigration/Status")
      if (res.data.status === "ok") {
        setStatus(res.data.response)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => {
    bindStatus()
  }, [bindStatus])

  // Refresco automático mientras la migración está corriendo. Se apaga solo cuando
  // no queda nada pendiente para no golpear la API sin razón.
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    const enMarcha =
      autoRefresh && !!status &&
      (status.progreso.pendientes > 0 || status.progreso.blobsPorVaciar > 0)

    if (enMarcha) {
      timerRef.current = setInterval(() => bindStatus(true), 5000)
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [autoRefresh, status, bindStatus])

  const doAction = async (accion: "Pause" | "Resume" | "RunOnce") => {
    try {
      setActing(true)
      const res = await API.getAction(`FotoMigration/${accion}`)
      if (res.data.status === "ok") {
        if (accion === "RunOnce") {
          const r = res.data.response
          toast.success(`Lote: ${r.migradas} migradas, ${r.fallidas} fallidas.`)
        } else {
          toast.success(res.data.response)
        }
        await bindStatus(true)
      } else {
        toast.error(res.data.response ?? "No se pudo completar la acción.")
      }
    } catch (ex: any) {
      toast.error(ex?.response?.data?.response ?? "Error ejecutando la acción.")
    } finally {
      setActing(false)
    }
  }

  const doVerify = async () => {
    try {
      setVerifying(true)
      setVerifyResult(null)
      const res = await API.getAction("FotoMigration/Verify?muestra=500")
      if (res.data.status === "ok") {
        setVerifyResult(res.data.response)
        if (res.data.response.integra) {
          toast.success("Muestra íntegra: todos los archivos existen en disco.")
        } else {
          toast.warn("La muestra encontró archivos faltantes o vacíos.")
        }
      } else {
        toast.error(res.data.response ?? "No se pudo verificar.")
      }
    } catch (ex: any) {
      toast.error(ex?.response?.data?.response ?? "Error verificando.")
    } finally {
      setVerifying(false)
    }
  }

  if (loading && !status) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    )
  }

  if (!status) {
    return (
      <Card className="mb-3">
        <Card.Body>
          <p className="mb-2">No se pudo leer el estado de la migración de fotos.</p>
          <Button size="sm" variant="outline-secondary" onClick={() => bindStatus()}>
            Reintentar
          </Button>
        </Card.Body>
      </Card>
    )
  }

  const { progreso, ritmo, ultimoLote, acumulado, configuracion, disco } = status
  const copiaCompleta = progreso.pendientes === 0 && progreso.total > 0
  const terminada = copiaCompleta && progreso.blobsPorVaciar === 0

  return (
    <>
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <b>Migración de fotos a filesystem</b>
            <div className="text-muted small">
              Saca los blobs de PadronFotos.Fotos a archivos y vacía la columna Imagen
              solo después de verificar cada archivo en disco.
            </div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            {!status.habilitado && <Badge bg="secondary">Deshabilitada</Badge>}
            {status.habilitado && status.enPausa && <Badge bg="warning" text="dark">En pausa</Badge>}
            {status.habilitado && !status.enPausa && !terminada && <Badge bg="success">Corriendo</Badge>}
            {copiaCompleta && !terminada && <Badge bg="info">Copiada, falta vaciar</Badge>}
            {terminada && <Badge bg="primary">Completada</Badge>}
          </div>
        </Card.Header>

        <Card.Body>
          <div className="mb-3">
            <div className="d-flex justify-content-between small mb-1">
              <span>
                {formatNumber(progreso.migradas)} de {formatNumber(progreso.total)} fotos
              </span>
              <b>{progreso.porcentaje}%</b>
            </div>
            <ProgressBar
              now={progreso.porcentaje}
              variant={terminada ? "primary" : "success"}
              style={{ height: "1.4rem" }}
            />
            <div className="text-muted small mt-1">
              Faltan {formatNumber(progreso.pendientes)}
              {ritmo.horasRestantesEstimadas != null && !terminada && (
                <> · quedan ~{ritmo.horasRestantesEstimadas} h al ritmo actual</>
              )}
            </div>
          </div>

          <Row className="g-3 mb-3">
            <Col xs={6} md={3}>
              <div className="text-muted small">Fase</div>
              <div className="h6 mb-0 text-capitalize">{progreso.faseActual}</div>
            </Col>
            <Col xs={6} md={3}>
              <div className="text-muted small">Ritmo</div>
              <div className="h5 mb-0">
                {ritmo.fotosPorSegundo != null ? `${ritmo.fotosPorSegundo}/s` : "—"}
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="text-muted small">Escrito a disco</div>
              <div className="h5 mb-0">{formatNumber(Math.round(ritmo.megabytesEscritos))} MB</div>
            </Col>
            <Col xs={6} md={3}>
              <div className="text-muted small">Fallidas acumuladas</div>
              <div className={`h5 mb-0 ${acumulado.fallidas > 0 ? "text-danger" : ""}`}>
                {formatNumber(acumulado.fallidas)}
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="text-muted small">Blobs por vaciar</div>
              <div className={`h5 mb-0 ${progreso.blobsPorVaciar > 0 ? "text-warning" : ""}`}>
                {formatNumber(progreso.blobsPorVaciar)}
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="text-muted small">Espacio libre</div>
              <div className="h5 mb-0">
                {disco ? `${formatNumber(disco.libreGb)} GB` : "—"}
              </div>
            </Col>
          </Row>

          <div className="d-flex gap-2 flex-wrap">
            {status.enPausa ? (
              <Button size="sm" variant="success" disabled={acting} onClick={() => doAction("Resume")}>
                Reanudar
              </Button>
            ) : (
              <Button size="sm" variant="warning" disabled={acting || terminada} onClick={() => doAction("Pause")}>
                Pausar
              </Button>
            )}
            <Button size="sm" variant="outline-primary" disabled={acting || terminada} onClick={() => doAction("RunOnce")}>
              {acting ? <Spinner size="sm" animation="border" /> : "Correr un lote"}
            </Button>
            <Button size="sm" variant="outline-secondary" disabled={verifying} onClick={doVerify}>
              {verifying ? <Spinner size="sm" animation="border" /> : "Verificar muestra"}
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => bindStatus()}>
              Refrescar
            </Button>
            <Button
              size="sm"
              variant={autoRefresh ? "outline-dark" : "outline-secondary"}
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              Auto: {autoRefresh ? "sí" : "no"}
            </Button>
          </div>

          {status.ultimoError && (
            <div className="alert alert-danger mt-3 mb-0 py-2 small">
              <b>Último error:</b> {status.ultimoError}
            </div>
          )}
        </Card.Body>
      </Card>

      <Row className="g-3">
        <Col md={6}>
          <Card className="h-100">
            <Card.Header><b>Último lote</b></Card.Header>
            <Card.Body>
              <Table size="sm" borderless className="mb-0">
                <tbody>
                  <tr><td className="text-muted">Migradas</td><td className="text-end">{formatNumber(ultimoLote.migradas)}</td></tr>
                  <tr><td className="text-muted">Fallidas</td><td className="text-end">{formatNumber(ultimoLote.fallidas)}</td></tr>
                  <tr><td className="text-muted">Duración</td><td className="text-end">{ultimoLote.segundos}s</td></tr>
                  <tr><td className="text-muted">Última cédula</td><td className="text-end font-monospace">{ultimoLote.ultimaCedula ?? "—"}</td></tr>
                  <tr><td className="text-muted">Última corrida</td><td className="text-end">{status.ultimaCorrida ? new Date(status.ultimaCorrida).toLocaleString("es-DO") : "—"}</td></tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="h-100">
            <Card.Header><b>Configuración</b></Card.Header>
            <Card.Body>
              <Table size="sm" borderless className="mb-0">
                <tbody>
                  <tr><td className="text-muted">Raíz</td><td className="text-end font-monospace small">{configuracion.raiz || <span className="text-danger">sin configurar</span>}</td></tr>
                  <tr><td className="text-muted">Tamaño de lote</td><td className="text-end">{configuracion.tamanoLote}</td></tr>
                  <tr><td className="text-muted">Pausa entre lotes</td><td className="text-end">{configuracion.pausaEntreLotesMs} ms</td></tr>
                  <tr><td className="text-muted">Vacía el blob</td><td className="text-end">{configuracion.vaciaBlob ? "sí" : "no (solo copia)"}</td></tr>
                  <tr><td className="text-muted">Verifica escritura</td><td className="text-end">{configuracion.verificaEscritura ? "sí" : "no"}</td></tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {verifyResult && (
        <Card className="mt-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <b>Verificación de integridad</b>
            <Badge bg={verifyResult.integra ? "success" : "danger"}>
              {verifyResult.integra ? "Íntegra" : "Con faltantes"}
            </Badge>
          </Card.Header>
          <Card.Body>
            <p className="mb-2">
              Se revisaron {formatNumber(verifyResult.verificadas)} cédulas marcadas como migradas:
              {" "}<b>{formatNumber(verifyResult.faltantes)}</b> sin archivo en disco,
              {" "}<b>{formatNumber(verifyResult.vacias)}</b> con archivo vacío.
            </p>
            {verifyResult.ejemplosFaltantes.length > 0 && (
              <div className="small">
                <b>Ejemplos faltantes:</b>{" "}
                <span className="font-monospace">{verifyResult.ejemplosFaltantes.join(", ")}</span>
              </div>
            )}
            {verifyResult.ejemplosVacias.length > 0 && (
              <div className="small mt-1">
                <b>Ejemplos vacíos:</b>{" "}
                <span className="font-monospace">{verifyResult.ejemplosVacias.join(", ")}</span>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </>
  )
}

export default AdminFotoMigrationComponent
